import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AccountLayout from "../components/AccountLayout.jsx";
import StripeConnectCard from "../components/billing/StripeConnectCard.jsx";
import ConnectBalancePanel from "../components/billing/ConnectBalancePanel.jsx";
import PayoutHistoryList from "../components/billing/PayoutHistoryList.jsx";
import ConnectRecentActivityList from "../components/billing/ConnectRecentActivityList.jsx";
import ClientPurchasesList from "../components/billing/ClientPurchasesList.jsx";
import StatusPill from "../components/billing/StatusPill.jsx";
import InvoiceHistoryList from "../components/billing/InvoiceHistoryList.jsx";
import { useSubscription } from "../hooks/useSubscription.js";
import { useStripeConnect } from "../hooks/useStripeConnect.js";
import { useCoachConnectSummary } from "../hooks/useCoachConnectSummary.js";
import { useCoachClientBilling } from "../hooks/useCoachClientBilling.js";
import { useCoachClientPlans } from "../hooks/useCoachClientPlans.js";
import { usePlanCatalog } from "../hooks/usePlanCatalog.js";
import { isPayingHeycoachSubscriber } from "../lib/heycoachSubscription.js";
import { formatSubscriptionAmount } from "../lib/money.js";
import {
  BillingPlanRowSkeleton,
} from "../components/loading/SkeletonBlocks.jsx";
import { fetchInvoicesViaGateway, createCustomerPortalSession } from "../lib/stripe.js";
import { consumePendingPortalReturn, stashPendingPortalReturn } from "../lib/portalReturn.js";
import { formatBillingInstant } from "../lib/stripeInstant.js";

function formatUnknownPlanLabel(planId) {
  return String(planId)
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanizeSubscriptionStatus(status) {
  const s = String(status ?? "").trim().toLowerCase();
  const map = {
    active: "Active",
    trialing: "Trialing",
    past_due: "Past due",
    paused: "Paused",
    canceled: "Canceled",
  };
  return map[s] ?? (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
}

export default function BillingPage() {
  const {
    subscription,
    isLoading: subLoading,
    error: subError,
    refetch: refetchSubscription,
  } = useSubscription();
  const { findTier } = usePlanCatalog({ audience: "coach" });
  const stripeConnect = useStripeConnect();
  const {
    summary: connectSummary,
    isLoading: connectSummaryLoading,
    error: connectSummaryError,
  } = useCoachConnectSummary();
  const {
    purchases: clientPurchases,
    isLoading: clientPurchasesLoading,
    error: clientPurchasesError,
    connectReady,
  } = useCoachClientBilling();
  const { plans: coachClientPlans } = useCoachClientPlans({ includeArchived: true });
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    const connectParam = searchParams.get("connect");
    if (connectParam !== "return" && connectParam !== "refresh") return;

    stripeConnect.refetch();
    refetchSubscription();
    const next = new URLSearchParams(searchParams);
    next.delete("connect");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, stripeConnect.refetch, refetchSubscription]);

  useEffect(() => {
    if (consumePendingPortalReturn() !== "/billing") return;
    refetchSubscription();
  }, [refetchSubscription]);

  useEffect(() => {
    let cancelled = false;
    setInvLoading(true);
    fetchInvoicesViaGateway(50)
      .then((data) => {
        if (!cancelled) setInvoices(data.invoices ?? []);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setInvLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const rawPlanId =
    subscription?.planId != null && String(subscription.planId).trim()
      ? String(subscription.planId).trim()
      : null;
  const tierPlan = rawPlanId ? findTier(rawPlanId) : null;
  const subscriptionLooksPaid =
    Boolean(subscription) && isPayingHeycoachSubscriber(subscription);
  const planTitle = subLoading
    ? "—"
    : subError && !subscription
      ? "Could not load plan"
      : subscriptionLooksPaid
        ? (tierPlan?.name ?? (rawPlanId ? formatUnknownPlanLabel(rawPlanId) : "Your subscription"))
        : subscription
          ? "No active plan"
          : "None";

  const subscriptionPriceLabel = formatSubscriptionAmount(subscription);
  const planNameById = Object.fromEntries(
    (coachClientPlans ?? []).map((plan) => [plan.id, plan.name]),
  );

  async function openBillingPortal() {
    setPortalBusy(true);
    try {
      const { url } = await createCustomerPortalSession();
      if (url) {
        stashPendingPortalReturn("/billing");
        window.location.assign(url);
      }
    } catch (e) {
      alert(e?.message ?? String(e));
    } finally {
      setPortalBusy(false);
    }
  }

  return (
    <AccountLayout>
      <section className="rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">
              HeyCoach plan
            </h2>
            <p className="mt-1 max-w-xl text-xs text-hc-text-dim">
              Your platform subscription for running your coaching business on HeyCoach.
            </p>
          </div>
          {subscriptionLooksPaid ? (
            <StatusPill variant="success">{humanizeSubscriptionStatus(subscription.status)}</StatusPill>
          ) : subscription ? (
            <StatusPill variant="warning">{humanizeSubscriptionStatus(subscription.status) || "Inactive"}</StatusPill>
          ) : null}
        </div>

        {subError && (
          <p className="mt-3 rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error break-words">
            {subError}
          </p>
        )}

        {subLoading ? (
          <BillingPlanRowSkeleton />
        ) : (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-[14px] border border-hc-surface-border bg-hc-page-shell px-4 py-3.5">
            <div>
              <p className="text-lg font-semibold tracking-[-0.15px] text-hc-text">{planTitle}</p>
              {subscriptionLooksPaid && subscriptionPriceLabel ? (
                <p className="mt-0.5 text-xs text-hc-text-dim">{subscriptionPriceLabel}</p>
              ) : null}
              {subscriptionLooksPaid && subscription?.currentPeriodEnd && (
                <p className="mt-0.5 text-xs text-hc-text-dim">
                  {subscription.cancelAtPeriodEnd ? "Cancels" : "Renews"}{" "}
                  {formatBillingInstant(subscription.currentPeriodEnd) || "—"}
                </p>
              )}
              {subscriptionLooksPaid && subscription?.cancelAtPeriodEnd ? (
                <p className="mt-2 text-xs text-hc-warning">
                  Your HeyCoach plan will cancel at the end of the current billing period.
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {subscriptionLooksPaid ? (
                <button
                  type="button"
                  disabled={portalBusy}
                  onClick={openBillingPortal}
                  className="rounded-[10px] border border-hc-surface-border-strong px-4 py-2 text-sm font-medium text-hc-text-muted transition hover:bg-white/5 disabled:opacity-50"
                >
                  {portalBusy ? "Opening…" : "Manage subscription"}
                </button>
              ) : (
                <Link
                  to="/plan"
                  className="rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  Choose a plan
                </Link>
              )}
            </div>
          </div>
        )}
      </section>

      <StripeConnectCard
        connect={stripeConnect.connect}
        isLoading={stripeConnect.isLoading}
        error={stripeConnect.error ?? connectSummaryError}
        actionBusy={stripeConnect.actionBusy}
        onBeginOnboarding={stripeConnect.beginOnboarding}
        onOpenDashboard={stripeConnect.openDashboard}
      />

      <ConnectBalancePanel
        balance={connectSummary?.balance}
        connect={stripeConnect.connect}
        isLoading={connectSummaryLoading}
      />

      {connectReady ? (
        <>
          <ConnectRecentActivityList
            activity={connectSummary?.recentActivity}
            currency={connectSummary?.balance?.currency ?? "cad"}
            isLoading={connectSummaryLoading}
            className="mt-4"
          />
          <PayoutHistoryList
            payouts={connectSummary?.payouts ?? []}
            isLoading={connectSummaryLoading}
            className="mt-4"
          />
          <ClientPurchasesList
            purchases={clientPurchases}
            planNameById={planNameById}
            isLoading={clientPurchasesLoading}
            className="mt-4"
          />
          {clientPurchasesError ? (
            <p className="mt-3 text-xs text-hc-error">{clientPurchasesError}</p>
          ) : null}
        </>
      ) : null}

      <InvoiceHistoryList
        invoices={invoices}
        isLoading={invLoading}
        title="HeyCoach invoices"
        subtitle="Invoices for your HeyCoach platform subscription."
      />
    </AccountLayout>
  );
}
